/**
 * useUserClubsQuery Hook
 *
 * Fetches the authenticated user's enrolled clubs (with `isHome` and `settings`).
 *
 * This is the canonical "enrolled clubs" source for the public apps. The clubs are
 * sourced from `GET /supabase-auth/me`, which returns full club rows plus the
 * `isHome` flag — unlike login/register, which return a minimal club shape.
 *
 * Tenant-scoped: enrolled clubs are shared across all of the tenant's clubs and
 * should persist when the visited club changes (each club is its own URL). Uses the
 * existing `queryKeys.tenant.userClubs` key.
 *
 * @module club/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { authService, useAuth } from '~/services/auth';
import type { EnrolledClub } from '~/services/auth';
import { useTenantHandle } from '~/services/appRuntime';

/**
 * Fetch the authenticated user's enrolled clubs.
 *
 * Returns the full React Query result plus a convenience `clubs` array.
 *
 * @example
 * ```tsx
 * const { clubs, isLoading } = useUserClubsQuery();
 * const homeClub = clubs.find(c => c.isHome);
 * ```
 */
export function useUserClubsQuery() {
  const { user, isAuthenticated } = useAuth();
  const [tenantHandle] = useTenantHandle();
  const userId = user?.id != null ? Number(user.id) : null;

  const query = useQuery<EnrolledClub[], Error>({
    queryKey:
      tenantHandle && userId !== null
        ? queryKeys.tenant.userClubs(tenantHandle, userId)
        : queryKeys.disabled.user(),
    queryFn: async () => {
      const profile = await authService.getProfile();
      return profile.clubs ?? [];
    },
    enabled: isAuthenticated && !!tenantHandle && userId !== null,
    // Enrolled clubs rarely change mid-session.
    ...cachePresets.stable,
  });

  return {
    ...query,
    clubs: query.data ?? [],
  };
}
